import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Images, MapPin, Calendar } from "lucide-react";
import LazyImage from "@/components/LazyImage";

const BASE = "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts";

const ACETECH_IMAGES = [
  "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/med9ygov_image.png",
  `${BASE}/is5r07tm_1.jpeg`,
  `${BASE}/nt6kk1go_2.jpeg`,
  `${BASE}/k4ups1xn_3.jpeg`,
  `${BASE}/uj4dtqvg_4.jpeg`,
  `${BASE}/4ahvf4p4_5.jpeg`,
  `${BASE}/nabok4qx_6.jpeg`,
  `${BASE}/h4ecq5ny_7.jpeg`,
  `${BASE}/1wikqy6q_8.jpeg`,
  `${BASE}/bv8srkrs_9.jpeg`,
  `${BASE}/ffuqlt2y_10.jpeg`,
  `${BASE}/nl95hthh_11.jpeg`,
  `${BASE}/aa6zaibd_12.jpeg`,
  `${BASE}/sx4zyq1d_13.jpeg`,
  `${BASE}/nm0e1tvd_14.jpeg`,
  `${BASE}/bl6lm8x4_15.jpeg`,
  `${BASE}/w9w25cdz_16.jpeg`,
  `${BASE}/nnf3ihro_17.jpeg`,
];

const INDEX_IMAGES = [
  `${BASE}/m54dfne6_Cover%20Page.jpeg`,
  `${BASE}/rjaxrrp7_1.jpeg`,
  `${BASE}/05c0xrgw_2.jpeg`,
  `${BASE}/4pf6n15c_WhatsApp%20Image%202026-03-27%20at%203.33.15%20PM%20%281%29.jpeg`,
  `${BASE}/f3w383ec_WhatsApp%20Image%202026-03-27%20at%203.33.17%20PM%20%281%29.jpeg`,
  `${BASE}/gk0wexys_WhatsApp%20Image%202026-03-27%20at%203.33.20%20PM.jpeg`,
  `${BASE}/zlsmaljc_WhatsApp%20Image%202026-03-27%20at%203.33.20%20PM%20%281%29.jpeg`,
  `${BASE}/xlbky13k_WhatsApp%20Image%202026-03-27%20at%203.33.19%20PM%20%282%29.jpeg`,
  `${BASE}/vej3vm74_WhatsApp%20Image%202026-03-27%20at%203.33.19%20PM%20%281%29.jpeg`,
  `${BASE}/sbmg1h67_WhatsApp%20Image%202026-03-27%20at%203.33.21%20PM%20%281%29.jpeg`,
  `${BASE}/drozbui3_WhatsApp%20Image%202026-03-27%20at%203.33.24%20PM.jpeg`,
  `${BASE}/n7tnom4p_WhatsApp%20Image%202026-03-27%20at%203.33.21%20PM%20%282%29.jpeg`,
  `${BASE}/dr5y60ia_WhatsApp%20Image%202026-03-27%20at%203.33.21%20PM.jpeg`,
  `${BASE}/rc35x0bf_WhatsApp%20Image%202026-03-27%20at%203.33.22%20PM%20%282%29.jpeg`,
  `${BASE}/0rnjsp2j_WhatsApp%20Image%202026-03-27%20at%203.33.24%20PM%20%281%29.jpeg`,
  `${BASE}/9rduy6jf_WhatsApp%20Image%202026-03-27%20at%203.33.29%20PM%20%283%29.jpeg`,
  `${BASE}/7gvj42pk_WhatsApp%20Image%202026-03-27%20at%203.33.26%20PM%20%281%29.jpeg`,
  `${BASE}/kfne2i9b_WhatsApp%20Image%202026-03-27%20at%203.33.28%20PM%20%282%29.jpeg`,
];

const EXHIBITIONS = [
  {
    id: "acetech",
    name: "ACETECH 2020",
    headline: "Showcasing Innovation at Acetech Mumbai",
    description: "Our modular systems were on display for architects and facility managers, highlighting our rapid-deployment office solutions.",
    location: "Mumbai",
    coverImage: ACETECH_IMAGES[0],
    images: ACETECH_IMAGES,
  },
  {
    id: "index",
    name: "INDEX 2020",
    headline: "Defining Workspace Standards at Index Mumbai",
    description: "Connecting with corporate decision-makers and interior designers, we demonstrated our end-to-end office furniture capabilities at a design and architecture expo.",
    location: "Mumbai",
    coverImage: INDEX_IMAGES[0],
    images: INDEX_IMAGES,
  },
];

function GalleryModal({ exhibition, onClose }) {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      if (selectedIndex !== null) setSelectedIndex(null);
      else onClose();
    }
    if (selectedIndex !== null) {
      if (e.key === "ArrowRight") setSelectedIndex((prev) => (prev + 1) % exhibition.images.length);
      if (e.key === "ArrowLeft") setSelectedIndex((prev) => (prev - 1 + exhibition.images.length) % exhibition.images.length);
    }
  }, [selectedIndex, exhibition.images.length, onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [handleKeyDown]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] bg-black flex flex-col"
      data-testid="gallery-modal"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 shrink-0">
        <div>
          <h3 className="text-white font-bold text-lg">{exhibition.name}</h3>
          <p className="text-white/60 text-sm">{exhibition.images.length} Photos</p>
        </div>
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          data-testid="gallery-close-btn"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Full-size preview */}
      <AnimatePresence mode="wait">
        {selectedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-10 bg-black/98 flex items-center justify-center"
            data-testid="image-preview-overlay"
          >
            <button
              onClick={() => setSelectedIndex(null)}
              className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              data-testid="preview-close-btn"
            >
              <X className="w-6 h-6" />
            </button>

            <button
              onClick={() => setSelectedIndex((prev) => (prev - 1 + exhibition.images.length) % exhibition.images.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors"
              data-testid="preview-prev-btn"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <button
              onClick={() => setSelectedIndex((prev) => (prev + 1) % exhibition.images.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition-colors"
              data-testid="preview-next-btn"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <motion.img
              key={selectedIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              src={exhibition.images[selectedIndex]}
              alt={`Ezeefit Office Systems at ${exhibition.name} furniture exhibition - Photo ${selectedIndex + 1}`}
              className="max-w-[90vw] max-h-[85vh] object-contain"
            />

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm font-mono">
              {selectedIndex + 1} / {exhibition.images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Grid */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-6xl mx-auto grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {exhibition.images.map((img, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: idx * 0.03 }}
              className="relative aspect-square cursor-pointer group overflow-hidden"
              onClick={() => setSelectedIndex(idx)}
              data-testid={`gallery-image-${idx}`}
            >
              <LazyImage
                src={img}
                alt={`Ezeefit stall at ${exhibition.name} office furniture exhibition Mumbai - Gallery image ${idx + 1}`}
                containerClassName="absolute inset-0"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                <span className="text-white font-mono text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                  {idx + 1}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function Exhibitions() {
  const [activeGallery, setActiveGallery] = useState(null);

  return (
    <section className="py-24 bg-[#0F172A] relative overflow-hidden" data-testid="exhibitions-section">
      {/* Subtle background texture */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
            Industry Presence
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Exhibitions Participated
          </h2>
          <p className="text-base md:text-lg text-[#94A3B8] max-w-2xl mx-auto">
            We showcase our modular furniture systems at architecture and design exhibitions, connecting with architects, designers, and corporate decision-makers.
          </p>
        </motion.div>

        {/* Exhibition Cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {EXHIBITIONS.map((exhibition, index) => (
            <motion.div
              key={exhibition.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="group cursor-pointer"
              onClick={() => setActiveGallery(exhibition)}
              data-testid={`exhibition-card-${exhibition.id}`}
            >
              {/* Image Container */}
              <div className="relative h-72 md:h-80 overflow-hidden">
                <img src={exhibition.coverImage}
                  alt={`Ezeefit Office Systems exhibition stall at ${exhibition.name} Mumbai - Modular furniture showcase`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-transparent" />

                {/* Tag */}
                <div className="absolute top-5 left-5">
                  <span className="bg-[#F97316] text-white text-[11px] font-bold tracking-widest uppercase px-4 py-2 inline-block">
                    {exhibition.name}
                  </span>
                </div>

                {/* Bottom Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-white text-xl md:text-2xl font-bold mb-2">
                    {exhibition.headline}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed line-clamp-2">
                    {exhibition.description}
                  </p>
                  <div className="flex items-center gap-4 mt-3">
                    <div className="flex items-center gap-1.5 text-white/50 text-xs">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{exhibition.location}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-white/50 text-xs">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>Annual</span>
                    </div>
                  </div>
                </div>

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-[#F97316]/0 group-hover:bg-[#F97316]/10 transition-colors duration-500 flex items-center justify-center">
                  <span className="text-white font-semibold text-sm bg-[#F97316] px-5 py-2.5 opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                    View Gallery
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust Ticker */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-14 relative overflow-hidden py-4 border-t border-b border-white/10"
          data-testid="trust-ticker"
        >
          <div className="ticker-track flex whitespace-nowrap">
            {[...Array(3)].map((_, repeatIdx) => (
              <span key={repeatIdx} className="ticker-content inline-flex items-center gap-8 px-4">
                <span className="text-[#94A3B8] text-sm italic">
                  Modular office furniture on display at design exhibitions.
                </span>
                <span className="text-[#F97316] text-xs font-mono">ACETECH</span>
                <span className="w-1 h-1 bg-[#F97316] rounded-full inline-block" />
                <span className="text-[#F97316] text-xs font-mono">INDEX</span>
                <span className="w-1 h-1 bg-[#F97316] rounded-full inline-block" />
                <span className="text-[#94A3B8] text-sm italic">
                  Showcasing modular workspace systems to architects and facility managers.
                </span>
                <span className="w-1 h-1 bg-[#F97316] rounded-full inline-block" />
              </span>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-8 text-center"
        >
          <p className="text-[#94A3B8] text-sm">
            Want to meet us at an upcoming exhibition?{" "}
            <a href="/contact" className="text-[#F97316] font-semibold hover:underline">
              Get in touch
            </a>{" "}
            for booth location and schedule.
          </p>
        </motion.div>
      </div>

      {/* Gallery Modal */}
      <AnimatePresence>
        {activeGallery && (
          <GalleryModal
            exhibition={activeGallery}
            onClose={() => setActiveGallery(null)}
          />
        )}
      </AnimatePresence>

      {/* Ticker Animation Styles */}
      <style>{`
        .ticker-track {
          animation: ticker-scroll 30s linear infinite;
        }
        .ticker-track:hover {
          animation-play-state: paused;
        }
        @keyframes ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
      `}</style>
    </section>
  );
}
